import { DownloadService } from "client/services/Download/DownloadService";
import { ga } from "client/utils/adsense/ga";
import { gtag } from "client/utils/adsense/gtag";
import { downloadFromLink } from "client/utils/downloadFromLink";
import { getFCUrl } from "client/utils/getFCUrl";
import { compressSelector } from "store/features/compress/selectors/compressSelector";
import { CompressImage } from "store/features/compress/types";
import { getCompressedImages } from "store/features/compress/utils/getCompressedImages";
import { getUnCompressedImages } from "store/features/compress/utils/getUnCompressedImages";
import { useAppDispatch, useAppSelector } from "store/store";
import { downloadImage } from "client/utils/downloadImage";
import { sizeToBytes } from "client/utils/sizeToBytes";
import { setDownloadButtonState } from "store/features/compress/actions";
import type { Task } from "@freeconvert/freeconvert-node/dist/types";

export const useDownloadAll = () => {
    const { images, downloadButtonState } = useAppSelector(compressSelector);
    const isExporting = downloadButtonState.state === "exporting";
    const isDownloading = downloadButtonState.state === "downloading";
    const downloadProgress = downloadButtonState.progress;
    const dispatch = useAppDispatch();

    const isActive =
        !isExporting &&
        !isDownloading &&
        getCompressedImages(images).length > 0 &&
        getUnCompressedImages(images).length === 0;

    const handleDownloadAllAnalytics = () => {
        ga("send", {
            hitType: "pageview",
            page: "/vp/image_compressor/export_started/",
            title: "Image Compressor Export Started",
        });

        ///event
        gtag("event", "Export Started", {
            event_category: "Image Compressor",
            event_label: "Editor Page",
        });
    };

    const handleDownloadAll = () => {
        if (!isActive) return;
        const compressedImages = getCompressedImages(images);
        if (compressedImages.length === 0) return;
        handleDownloadAllAnalytics();

        compressedImages.length === 1
            ? handleSingleImageDownload(compressedImages[0])
            : handleMultipleImagesDownload(compressedImages);
    };

    const handleSingleImageDownload = (compressedImage: CompressImage) =>
        downloadFromLink(getFCUrl(compressedImage.compressed?.src ?? ""), compressedImage.title, true);

    const handleMultipleImagesDownload = (compressedImages: CompressImage[]) => {
        const taskIds = (compressedImages.flatMap((image) => image.compressed?.job) || [])
            .flatMap((job) => job?.tasks)
            .filter((task): task is Task => !!task && task?.name === "exportTask")
            .map((task) => task?.id);

        dispatch(
            setDownloadButtonState({
                state: "exporting",
            }),
        );

        new DownloadService(taskIds, {
            onComplete: async (task) => {
                const url = task.result?.url?.toString() ?? "";
                dispatch(
                    setDownloadButtonState({
                        state: "downloading",
                        progress: 0,
                    }),
                );
                const { src } = await downloadImage(url, getTotalZipSize(compressedImages), (progress) => {
                    dispatch(
                        setDownloadButtonState({
                            state: "downloading",
                            progress,
                        }),
                    );
                });
                downloadFromLink(src, "compressed-images.zip");
                dispatch(
                    setDownloadButtonState({
                        state: "active",
                        progress: undefined,
                    }),
                );
            },
            onError: () => {
                dispatch(
                    setDownloadButtonState({
                        state: "active",
                        progress: undefined,
                    }),
                );
            },
            filename: "compressed-images.zip",
        });
    };

    const getTotalZipSize = (images: CompressImage[]) =>
        images.reduce((prevValue, _, index) => prevValue + sizeToBytes(images[index].compressed?.size ?? "0 KB"), 0);

    return { images, handleDownloadAll, isActive, isExporting, isDownloading, downloadProgress };
};
